import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import Button from '@ingka/button';
import useScrollToTop from '../../hooks/ScrollToTop';
import {
  getPageError,
  setBreadcrumbs,
  useAppContext,
} from '../../contextProviders/AppContext';
import Focus from '../../components/Focus';
import Seo from '../../components/Seo/Seo';
import { ERROR_PAGE } from '../../components/Seo/enums';
import ConditionalRender from '../../components/ConditionalRender';
import Image404 from './404.gif';

const Header = styled.h1`
  margin-top: var(--space-250);
  margin-bottom: var(--space-200);
  ${({ theme }) => `${theme.breakpoints.lg} {
    margin-top: var(--space-1350);
  }`}
`;

const Description = styled.p`
  ${({ theme }) => `${theme.breakpoints.lg} {
    max-width: 410px;
  }`}
`;

const StyledButton = styled(Button)`
  margin-top: var(--space-250);
`;

const FourOFourImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  & + h1 {
    margin-top: var(--space-400);
  }
`;

const getTexts = (code: number = -1) => {
  const handledErrorCodes = [400, 404, 500, 504];

  if (!code || !handledErrorCodes.includes(code)) {
    return { title: 'error.title', description: 'error.description' };
  }

  return {
    title: `error.title`,
    description: `error.description-${code}`,
  };
};

const getButtonConfig = (code: number = -1, history: any) => {
  switch (code) {
    case 404:
      return {
        onButtonClick: () => history.replace('/'),
        buttonText: 'result.backToAllEvents',
      };
    default:
      return {
        onButtonClick: () => history.goBack(),
        buttonText: 'error.tryAgainButton',
      };
  }
};

const ErrorPage = () => {
  const { t } = useTranslation();
  const { useSelector, dispatch } = useAppContext();
  const pageError = useSelector(getPageError);
  const history = useHistory();

  const { title, description } = getTexts(pageError?.code);
  const { onButtonClick, buttonText } = getButtonConfig(
    pageError?.code,
    history
  );

  useScrollToTop();

  useEffect(() => {
    dispatch(setBreadcrumbs([{ text: t('app.bcErrorPage') }]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Focus>
      <Seo page={ERROR_PAGE} canonicalPath="/" />
      <ConditionalRender predicate={pageError?.code === 404}>
        <FourOFourImageWrapper>
          <img src={Image404} alt="404" />
        </FourOFourImageWrapper>
      </ConditionalRender>
      <Header>{t(title)}</Header>
      <Description>{t(description)}</Description>
      <StyledButton onClick={onButtonClick} type="primary">
        {t(buttonText)}
      </StyledButton>
    </Focus>
  );
};

export default ErrorPage;
